/**
 * Connect — per-user app-state profile (OMON-212 → ADR-0027).
 *
 * User preferences are NOT FHIR (see ADR-0027). They are app-native UI state,
 * stored in a scoped preference store keyed on the Firebase Auth uid:
 *
 *   userPreferences/{uid}/apps/{APP_CODE}
 *
 * — located by ID (never queried), self-scoped by uid at the rule layer
 * (`request.auth.uid == uid`), no `subject`/`application` refs, no claim, no
 * composite index. This replaces the previous FHIR `_basic` `user-app-profile`
 * doc, which keyed on a `_practitioner` subject the strict rules couldn't accept
 * (OMON-250). The Flutter coordinator that shared the old `_basic` doc is being
 * retired in favour of this web Connect panel (ADR-0025), so there is no cross-app
 * writer to keep in sync.
 *
 * Preference shape: `filters.sensorsByEoc.{eocId}` = [{ propertyPath (ref), subtype }]
 * — a per-client sensor allowlist (OMON-234; empty/absent → show all lanes).
 * Writes are optimistic + merge + swallow-errors (a preference is not critical —
 * the next bootstrap reconciles).
 */
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type DocumentReference,
} from "firebase/firestore";
import { db, auth } from "../firebase";

const APP_CODE = "connect"; // userPreferences/{uid}/apps/connect

/** One saved sensor: the value-collection path + the channel subtype. */
export interface SensorFilterEntry {
  propertyPath: string; // "_values/{collectionId}"
  subtype: string; // channel identifier, e.g. "shellyTemp", "TSL2591"
}

// OMON-234: per-EoC saved sensor allowlists. Keyed by eocId so a per-client
// selection only affects that client (the pre-OMON-234 global list blanked every
// other client — 2026-06-29 outage).
let sensorKeysByEoc = new Map<string, Set<string>>();
let ready = false;

/** True once bootstrap has resolved the user's preference state. */
export function isProfileReady(): boolean {
  return ready;
}

/** The user's saved sensor allowlist FOR THIS CLIENT, as `${propertyPath}::${subtype}`
 *  keys. EMPTY = no filter saved for this eocId → callers show all its lanes. */
export function selectedSensorKeys(eocId: string): Set<string> {
  return sensorKeysByEoc.get(eocId) ?? new Set<string>();
}

function parseEntries(sensors: unknown): Set<string> {
  const keys = new Set<string>();
  if (Array.isArray(sensors)) {
    for (const s of sensors) {
      const ref = s?.propertyPath;
      const path = ref && typeof ref === "object" && typeof ref.path === "string" ? ref.path : null;
      if (path && typeof s?.subtype === "string") keys.add(`${path}::${s.subtype}`);
    }
  }
  return keys;
}

// ── swimlane device prefs (order + hidden), per client ──────────────────
// Same ADR-0027 store, sibling field `swimlane.byEoc.{eocId}`. `order` is
// PARTIAL by design: listed devices first in that order, unlisted append in
// natural (registry) order so NEW hardware shows up rather than vanishing;
// unknown ids are ignored so a removed device can never wedge the pref.

export interface SwimlaneDevicePrefs {
  order: string[];
  hidden: string[];
}

let swimlaneByEoc = new Map<string, SwimlaneDevicePrefs>();

/** The user's saved swimlane device prefs FOR THIS CLIENT (empty = defaults). */
export function swimlaneDevicePrefs(eocId: string): SwimlaneDevicePrefs {
  return swimlaneByEoc.get(eocId) ?? { order: [], hidden: [] };
}

function parseSwimlaneByEoc(data: DocumentData | undefined): Map<string, SwimlaneDevicePrefs> {
  const out = new Map<string, SwimlaneDevicePrefs>();
  const byEoc = (data?.swimlane as DocumentData | undefined)?.byEoc;
  if (byEoc && typeof byEoc === "object") {
    for (const [eocId, v] of Object.entries(byEoc as Record<string, unknown>)) {
      const rec = v as { order?: unknown; hidden?: unknown } | null;
      const order = Array.isArray(rec?.order)
        ? rec.order.filter((x): x is string => typeof x === "string")
        : [];
      const hidden = Array.isArray(rec?.hidden)
        ? rec.hidden.filter((x): x is string => typeof x === "string")
        : [];
      if (order.length > 0 || hidden.length > 0) out.set(eocId, { order, hidden });
    }
  }
  return out;
}

/**
 * Persist one client's device order/visibility. Optimistic in-memory update,
 * deep-merge write scoped to this eocId — mirrors saveSelectedSensors, and
 * like it, swallows errors (a preference is not critical; the next bootstrap
 * reconciles).
 */
export async function saveSwimlaneDevicePrefs(
  eocId: string,
  prefs: SwimlaneDevicePrefs,
): Promise<void> {
  if (prefs.order.length === 0 && prefs.hidden.length === 0) {
    swimlaneByEoc.delete(eocId);
  } else {
    swimlaneByEoc.set(eocId, { order: [...prefs.order], hidden: [...prefs.hidden] });
  }
  const ref = prefDocRef();
  if (!ref) return;
  try {
    await setDoc(
      ref,
      {
        swimlane: { byEoc: { [eocId]: { order: prefs.order, hidden: prefs.hidden } } },
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    console.warn("[connect] saveSwimlaneDevicePrefs failed", err);
  }
}

/** Build the per-EoC key map from `filters.sensorsByEoc` (OMON-234). */
function parseSensorKeysByEoc(data: DocumentData | undefined): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  const byEoc = (data?.filters as DocumentData | undefined)?.sensorsByEoc;
  if (byEoc && typeof byEoc === "object") {
    for (const [eocId, sensors] of Object.entries(byEoc as Record<string, unknown>)) {
      const keys = parseEntries(sensors);
      if (keys.size > 0) out.set(eocId, keys);
    }
  }
  return out;
}

/** The signed-in user's preference doc ref (`userPreferences/{uid}/apps/connect`),
 *  derived FRESH from auth.currentUser every time — never cached. auth.currentUser
 *  is often null right after page load, so caching a ref at bootstrap risked a
 *  permanent null that silently skipped every save's write. Null → not signed in. */
function prefDocRef(): DocumentReference | null {
  const uid = auth.currentUser?.uid;
  return uid ? doc(db, "userPreferences", uid, "apps", APP_CODE) : null;
}

/**
 * Idempotent load. Safe to call on every entry to the connect panel. Reads the
 * user's preference doc by id; the doc is created lazily on the first save.
 * Absent doc → no filters (show all lanes). We await the initial auth state first
 * — otherwise a load that races Firebase's session restore sees a null user,
 * loads nothing, and (pre-fix) cached a null ref that killed every later save.
 */
export async function bootstrapProfile(): Promise<void> {
  try {
    await auth.authStateReady();
    const ref = prefDocRef();
    if (!ref) {
      ready = false;
      return;
    }
    const snap = await getDoc(ref);
    sensorKeysByEoc = snap.exists() ? parseSensorKeysByEoc(snap.data()) : new Map();
    swimlaneByEoc = snap.exists() ? parseSwimlaneByEoc(snap.data()) : new Map();
    ready = true;
  } catch (err) {
    console.warn("[connect] profile bootstrap failed", err);
    ready = false;
  }
}

/**
 * Persist the user's shown-sensor allowlist for one client. Optimistic in-memory
 * update, then a deep-merge write to `filters.sensorsByEoc.{eocId}` (so it doesn't
 * clobber other clients' selections). Creates the doc on first save. Stores native
 * DocumentReferences for `propertyPath`.
 */
export async function saveSelectedSensors(
  eocId: string,
  entries: SensorFilterEntry[],
): Promise<void> {
  const seen = new Set<string>();
  const keys = new Set<string>();
  const sensors: Array<{ propertyPath: DocumentReference; subtype: string }> = [];
  for (const e of entries) {
    const key = `${e.propertyPath}::${e.subtype}`;
    if (seen.has(key)) continue;
    seen.add(key);
    keys.add(key);
    sensors.push({ propertyPath: doc(db, e.propertyPath), subtype: e.subtype });
  }
  // OMON-234: scope to THIS client. Empty selection → [] for this eocId
  // (parse treats an empty/absent list as "show all").
  if (keys.size === 0) sensorKeysByEoc.delete(eocId);
  else sensorKeysByEoc.set(eocId, keys);
  const ref = prefDocRef();
  if (!ref) return;
  try {
    await setDoc(
      ref,
      { filters: { sensorsByEoc: { [eocId]: sensors } }, updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    console.warn("[connect] saveSelectedSensors failed", err);
  }
}
